// ------------------------------------------------------------------
// ReportTableOfContents — a report's section list, as real navigation
// (story 4-81; P06 §4). Story 4-111 adds the classic dotted-leader page
// number ("Section name .......... 4").
//
// Entries come from the template's OWN section list (`contracts/reports.js
// #ReportTocEntry[]`, story 4-80), in template order — not from which
// sections happened to render content — so this matches what the template's
// author laid out even for a section an inspection left entirely unanswered.
//
// a11y: a `<nav>` landmark with a real accessible name (not a generic
// "Navigation" region a screen-reader user has to open to identify), and an
// ordered list because a table of contents' entries have a real order that
// is part of their meaning. `href` is optional: the web renderer can point
// each entry at the matching `<section id>` for real in-page jumps; a
// caller with no anchors (the PDF's plain-text mirror does not need one —
// see `pdfExport.service.js`) omits it and gets plain list items instead of
// dead links.
//
// ── The page number: real text, never a CSS `content: counter()` leader ────
// Same rule `ReportPageBadge.tsx`'s own header comment establishes for the
// paginated-preview badge, applied here too: a real `<span>` with real text,
// not CSS Paged Media's `target-counter()` (Chromium supports it, but
// generated `content` text is inconsistently exposed to screen readers
// across browser/AT combinations — the established pattern in this
// component family is "compute the real number in JS from real layout,
// render it as real DOM text"). The dotted LEADER between label and number
// (`toc-leader` below) IS purely decorative (`aria-hidden`) — unlike the
// number itself, it carries no information a screen reader needs, so CSS is
// the right tool for it (a `border-bottom` filling the flex gap, not a
// literal run of `.` characters, which would wrap unpredictably at
// arbitrary label lengths).
//
// ── `entry.page` may be genuinely absent ────────────────────────────────────
// `null`/`undefined` (content built before this story, an unassembled report
// whose page numbers are not resolved yet — `resolveTableOfContentsPageNumbers`
// only runs at report-assembly/PDF-generation time, never on a live read
// path, see that resolver's own header comment) renders the label with NO
// leader/number at all — never a guessed or placeholder number.
//
// ── `pendingPageNumbers` — the ONE caller that fills numbers in AFTER layout ─
// `PagedReportPreview.tsx` (the template editor's client-side Paged.js
// preview) cannot know page numbers until its OWN pagination pass has
// already run against this exact markup — a chicken-and-egg the plain
// backend-fed callers (`ReportDetailPage`/`ReviewDetailPage`/
// `SharedReportPage`, which already know `entry.page` before render) never
// hit. That ONE caller passes `pendingPageNumbers` so the dotted-leader row
// still renders (with an EMPTY, hooked `data-toc-page-for` number slot) even
// though every `entry.page` is still null at render time — `PagedReportPreview`
// then fills each slot's text in imperatively once its Paged.js run
// completes, the same "real DOM text once real layout exists" rule applied
// a step later. Every other caller leaves this off (default false), so its
// own render is unaffected.
export interface ReportTableOfContentsEntry {
  key: string;
  label: string;
  /** The section's real, resulting physical page number (1-based, cover page included) — `null`/absent renders no leader/number. */
  page?: number | null | undefined;
}

export interface ReportTableOfContentsProps {
  entries: ReportTableOfContentsEntry[];
  /** Section `id` to jump to, e.g. `(entry) => `#${entry.key}``. Omit for a plain (non-linked) list. */
  hrefFor?: (entry: ReportTableOfContentsEntry) => string;
  /** See this file's own header comment — `PagedReportPreview.tsx` only. */
  pendingPageNumbers?: boolean;
  className?: string;
}

export function ReportTableOfContents({
  entries,
  hrefFor,
  pendingPageNumbers = false,
  className = '',
}: ReportTableOfContentsProps): JSX.Element | null {
  if (entries.length === 0) return null;
  return (
    <nav aria-label="Table of contents" className={className}>
      <ol className="space-y-1">
        {entries.map((entry) => {
          const showPageSlot = entry.page != null || pendingPageNumbers;
          const row = (
            <span className="flex items-baseline gap-1">
              <span>{entry.label}</span>
              {showPageSlot && (
                <>
                  <span aria-hidden="true" className="toc-leader h-0 flex-1 -translate-y-[3px] border-b border-dotted border-border" />
                  <span
                    className="text-caption tabular-nums text-muted-foreground"
                    data-toc-page-for={pendingPageNumbers ? entry.key : undefined}
                  >
                    {entry.page ?? ''}
                  </span>
                </>
              )}
            </span>
          );
          return (
            <li key={entry.key} data-toc-entry-key={entry.key}>
              {hrefFor ? (
                <a
                  href={hrefFor(entry)}
                  className="focus-ring block rounded text-sm text-primary underline-offset-2 hover:underline"
                >
                  {row}
                </a>
              ) : (
                <span className="block text-sm text-foreground">{row}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
